import { SessionStorageService } from 'app/core/services/session-storage.service';
import { TimeZoneDateFnsService } from 'app/core/services/time-zone-date-fns.service';
import { DocumentListSpreadsheetExportTypeHelper } from 'app/shared/enum-helpers/document-list-spreadsheet-export-type.enum.helper';
import { SpreadsheetExportTypeHelper } from 'app/shared/enum-helpers/spreadsheet-export-type.enum.helper';
import { NotificationStatus } from 'app/shared/enums/notification-status.enum';
import { SpreadsheetExportFileType } from 'app/shared/enums/spreadsheet-export-file-type.enum';
import { SpreadsheetExportType } from 'app/shared/enums/spreadsheet-export-type.enum';
import { CustomDatePipe } from 'app/shared/pipes/custom-date.pipe';
import { DeliveryFormFilter } from 'app/shared/prothesis-api-filters/delivery-form-filter';
import { ReadSpreadsheetExportNotificationViewModel } from 'app/shared/prothesis-api-view-models/read-spreadsheet-export-notification.view-model';
import { buildNotificationCardTitle } from '../../build-notification-card-title';
import { SpreadsheetExportNotificationCardModel } from './spreadsheet-export-notification-card.model';

export class SpreadsheetExportNotificationCardModelHelper {
  public static build(): SpreadsheetExportNotificationCardModel {
    const spreadsheetExportNotificationCard = new SpreadsheetExportNotificationCardModel();
    return spreadsheetExportNotificationCard;
  }

  public static fromViewModel(
    readSpreadsheetExportNotificationViewModels: ReadSpreadsheetExportNotificationViewModel[],
    customDatePipe: CustomDatePipe,
  ): Array<SpreadsheetExportNotificationCardModel> {
    const spreadsheetExportNotificationCards = new Array<SpreadsheetExportNotificationCardModel>();
    readSpreadsheetExportNotificationViewModels.forEach((readSpreadsheetExportNotificationViewModel) => {
      const spreadsheetExportNotificationCard = this._createModel(readSpreadsheetExportNotificationViewModel, customDatePipe);
      spreadsheetExportNotificationCards.push(spreadsheetExportNotificationCard);
    });

    return spreadsheetExportNotificationCards;
  }

  public static fromViewModelSingle(
    readSpreadsheetExportNotificationViewModel: ReadSpreadsheetExportNotificationViewModel,
    customDatePipe: CustomDatePipe,
  ): SpreadsheetExportNotificationCardModel {
    return this._createModel(readSpreadsheetExportNotificationViewModel, customDatePipe);
  }

  private static _createModel(
    readSpreadsheetExportNotificationViewModel: ReadSpreadsheetExportNotificationViewModel,
    customDatePipe: CustomDatePipe,
  ): SpreadsheetExportNotificationCardModel {
    let notificationPrefixLabel = `Export Excel`;
    const allPdfFlag = readSpreadsheetExportNotificationViewModel.spreadsheetExportNotificationDetails.every(
      (x) => x.spreadsheetExportFileType === SpreadsheetExportFileType.Pdf,
    );

    const multipleFileFlag = readSpreadsheetExportNotificationViewModel.spreadsheetExportNotificationDetails.length > 1;
    const spreadsheetExportNotificationCard = new SpreadsheetExportNotificationCardModel();
    if (allPdfFlag) {
      notificationPrefixLabel = `PDF de liste`;
      spreadsheetExportNotificationCard.spreadsheetExportFileType = SpreadsheetExportFileType.Pdf;
    } else {
      spreadsheetExportNotificationCard.spreadsheetExportFileType = SpreadsheetExportFileType.Xlsx;
    }

    spreadsheetExportNotificationCard.colorClass = allPdfFlag ? 'notification-accent' : 'notification-green';
    spreadsheetExportNotificationCard.discriminator = readSpreadsheetExportNotificationViewModel.discriminator;
    spreadsheetExportNotificationCard.multipleFilesFlag = multipleFileFlag;
    spreadsheetExportNotificationCard.notificationDate = TimeZoneDateFnsService.toLaboratoryTimeZone(
      readSpreadsheetExportNotificationViewModel.notificationDate,
    );

    spreadsheetExportNotificationCard.readDate = TimeZoneDateFnsService.toLaboratoryTimeZone(
      readSpreadsheetExportNotificationViewModel.readDate,
    );
    spreadsheetExportNotificationCard.title = buildNotificationCardTitle(
      notificationPrefixLabel,
      readSpreadsheetExportNotificationViewModel.notificationDate,
      customDatePipe,
    );
    spreadsheetExportNotificationCard.messageLine1 = this.buildMessageLine1(readSpreadsheetExportNotificationViewModel);

    spreadsheetExportNotificationCard.statusFailedFlag = readSpreadsheetExportNotificationViewModel.status === NotificationStatus.Failed;
    spreadsheetExportNotificationCard.statusNothingToDoFlag =
      readSpreadsheetExportNotificationViewModel.status === NotificationStatus.NothingToDo;
    spreadsheetExportNotificationCard.statusPendingFlag = readSpreadsheetExportNotificationViewModel.status === NotificationStatus.Pending;
    spreadsheetExportNotificationCard.statusSucceedFlag = readSpreadsheetExportNotificationViewModel.status === NotificationStatus.Succeed;
    spreadsheetExportNotificationCard.uuid = readSpreadsheetExportNotificationViewModel.uuid;
    return spreadsheetExportNotificationCard;
  }

  private static buildMessageLine1(
    readSpreadsheetExportNotificationViewModel: ReadSpreadsheetExportNotificationViewModel,
  ): string | undefined {
    if (readSpreadsheetExportNotificationViewModel.spreadsheetExportNotificationDetails.length > 0) {
      if (readSpreadsheetExportNotificationViewModel.spreadsheetExportNotificationDetails.length === 1) {
        const spreadsheetExportNotificationDetail = readSpreadsheetExportNotificationViewModel.spreadsheetExportNotificationDetails[0];
        // A list export names itself in full ("PDF de la liste des factures"); it has no
        // simple/detailed variant to slot into the "PDF - Liste ... des" wording below.
        if (DocumentListSpreadsheetExportTypeHelper.isDocumentList(spreadsheetExportNotificationDetail.spreadsheetExportType)) {
          return DocumentListSpreadsheetExportTypeHelper.translateEnum(spreadsheetExportNotificationDetail.spreadsheetExportType);
        }

        const deliveryFormFilter = SessionStorageService.deserializeJson(spreadsheetExportNotificationDetail.filter) as DeliveryFormFilter;
        let messagePrefix =
          spreadsheetExportNotificationDetail.spreadsheetExportFileType === SpreadsheetExportFileType.Pdf ? 'PDF - Liste des ' : 'Excel - ';
        switch (spreadsheetExportNotificationDetail.spreadsheetExportType) {
          case SpreadsheetExportType.SimpleDetailedDeliveryForm:
          case SpreadsheetExportType.SimpleDetailedFittingForm:
          case SpreadsheetExportType.SimpleDetailedQuote:
          case SpreadsheetExportType.SimpleDetailedWorkForm:
            messagePrefix = 'PDF - Liste détaillée des ';
            break;
          case SpreadsheetExportType.SimpleDeliveryForm:
          case SpreadsheetExportType.SimpleFittingForm:
          case SpreadsheetExportType.SimpleQuote:
          case SpreadsheetExportType.SimpleWorkForm:
            messagePrefix = 'PDF - Liste simple des ';
        }

        return (
          messagePrefix +
          SpreadsheetExportTypeHelper.translateEnum(spreadsheetExportNotificationDetail.spreadsheetExportType, deliveryFormFilter)
        );
      } else {
        if (
          readSpreadsheetExportNotificationViewModel.spreadsheetExportNotificationDetails.every((spreadsheetExportNotificationDetail) =>
            [
              SpreadsheetExportType.Credit,
              SpreadsheetExportType.CreditDeliveryForm,
              SpreadsheetExportType.CreditDeliveryFormItem,
              SpreadsheetExportType.CreditDeliveryFormItemRawMaterial,
              SpreadsheetExportType.CreditFreeLine,
            ].includes(spreadsheetExportNotificationDetail.spreadsheetExportType),
          )
        ) {
          return "Excel multiples - Factures d'avoir";
        } else if (
          readSpreadsheetExportNotificationViewModel.spreadsheetExportNotificationDetails.every((spreadsheetExportNotificationDetail) =>
            [
              SpreadsheetExportType.DeliveryForm,
              SpreadsheetExportType.DeliveryFormItem,
              SpreadsheetExportType.DeliveryFormItemLaboratoryTask,
              SpreadsheetExportType.DeliveryFormItemRawMaterial,
            ].includes(spreadsheetExportNotificationDetail.spreadsheetExportType),
          )
        ) {
          if (
            readSpreadsheetExportNotificationViewModel.spreadsheetExportNotificationDetails.every((spreadsheetExportNotificationDetail) => {
              const deliveryFormFilter = SessionStorageService.deserializeJson(
                spreadsheetExportNotificationDetail.filter,
              ) as DeliveryFormFilter;
              if (deliveryFormFilter && deliveryFormFilter.invoicedFlag === true) {
                return true;
              } else {
                return false;
              }
            })
          ) {
            return 'Excel multiples - Bons facturés';
          } else if (
            readSpreadsheetExportNotificationViewModel.spreadsheetExportNotificationDetails.every((spreadsheetExportNotificationDetail) => {
              const deliveryFormFilter = SessionStorageService.deserializeJson(
                spreadsheetExportNotificationDetail.filter,
              ) as DeliveryFormFilter;
              if (deliveryFormFilter && deliveryFormFilter.proFormaFlag === true) {
                return true;
              } else {
                return false;
              }
            })
          ) {
            return 'Excel multiples - Bons sur pro forma';
          }
          return 'Excel multiples - Bons de livraison';
        } else if (
          readSpreadsheetExportNotificationViewModel.spreadsheetExportNotificationDetails.every((spreadsheetExportNotificationDetail) =>
            [
              SpreadsheetExportType.FittingForm,
              SpreadsheetExportType.FittingFormItem,
              SpreadsheetExportType.FittingFormItemLaboratoryTask,
              SpreadsheetExportType.FittingFormItemRawMaterial,
            ].includes(spreadsheetExportNotificationDetail.spreadsheetExportType),
          )
        ) {
          return "Excel multiples - Bons d'essayage";
        } else if (
          readSpreadsheetExportNotificationViewModel.spreadsheetExportNotificationDetails.every((spreadsheetExportNotificationDetail) =>
            [
              SpreadsheetExportType.Invoice,
              SpreadsheetExportType.InvoiceDeliveryForm,
              SpreadsheetExportType.InvoiceDeliveryFormItem,
              SpreadsheetExportType.InvoiceDeliveryFormItemRawMaterial,
              SpreadsheetExportType.InvoiceFreeLine,
            ].includes(spreadsheetExportNotificationDetail.spreadsheetExportType),
          )
        ) {
          return 'Excel multiples - Factures définitives';
        } else if (
          readSpreadsheetExportNotificationViewModel.spreadsheetExportNotificationDetails.every((spreadsheetExportNotificationDetail) =>
            [SpreadsheetExportType.InvoiceUnpaidReminder, SpreadsheetExportType.InvoiceUnpaidReminderMailing].includes(
              spreadsheetExportNotificationDetail.spreadsheetExportType,
            ),
          )
        ) {
          return 'Excel multiples - Relances';
        } else if (
          readSpreadsheetExportNotificationViewModel.spreadsheetExportNotificationDetails.every((spreadsheetExportNotificationDetail) =>
            [
              SpreadsheetExportType.ProForma,
              SpreadsheetExportType.ProFormaDeliveryForm,
              SpreadsheetExportType.ProFormaDeliveryFormItem,
              SpreadsheetExportType.ProFormaDeliveryFormItemRawMaterial,
            ].includes(spreadsheetExportNotificationDetail.spreadsheetExportType),
          )
        ) {
          return 'Excel multiples - Factures pro forma';
        } else if (
          readSpreadsheetExportNotificationViewModel.spreadsheetExportNotificationDetails.every((spreadsheetExportNotificationDetail) =>
            [SpreadsheetExportType.Quote, SpreadsheetExportType.QuoteItem, SpreadsheetExportType.QuoteItemLaboratoryTask].includes(
              spreadsheetExportNotificationDetail.spreadsheetExportType,
            ),
          )
        ) {
          return 'Excel multiples - Devis';
        } else if (
          readSpreadsheetExportNotificationViewModel.spreadsheetExportNotificationDetails.every((spreadsheetExportNotificationDetail) =>
            [
              SpreadsheetExportType.WorkForm,
              SpreadsheetExportType.WorkFormItem,
              SpreadsheetExportType.WorkFormItemLaboratoryTask,
              SpreadsheetExportType.WorkFormItemRawMaterial,
            ].includes(spreadsheetExportNotificationDetail.spreadsheetExportType),
          )
        ) {
          return 'Excel multiples - Bons de travail';
        } else if (
          readSpreadsheetExportNotificationViewModel.spreadsheetExportNotificationDetails.every((spreadsheetExportNotificationDetail) =>
            [SpreadsheetExportType.Customer, SpreadsheetExportType.CustomerContact, SpreadsheetExportType.CustomerUserCustomer].includes(
              spreadsheetExportNotificationDetail.spreadsheetExportType,
            ),
          )
        ) {
          return 'Excel multiples - Clients';
        } else if (
          readSpreadsheetExportNotificationViewModel.spreadsheetExportNotificationDetails.every((spreadsheetExportNotificationDetail) =>
            [SpreadsheetExportType.Manufacturer, SpreadsheetExportType.ManufacturerContact].includes(
              spreadsheetExportNotificationDetail.spreadsheetExportType,
            ),
          )
        ) {
          return 'Excel multiples - Fabricants';
        } else if (
          readSpreadsheetExportNotificationViewModel.spreadsheetExportNotificationDetails.every((spreadsheetExportNotificationDetail) =>
            [
              SpreadsheetExportType.Item,
              SpreadsheetExportType.ItemLaboratoryTaskTemplate,
              SpreadsheetExportType.ItemRate,
              SpreadsheetExportType.ItemRawMaterial,
            ].includes(spreadsheetExportNotificationDetail.spreadsheetExportType),
          )
        ) {
          return 'Excel multiples - Articles';
        } else if (
          readSpreadsheetExportNotificationViewModel.spreadsheetExportNotificationDetails.every((spreadsheetExportNotificationDetail) =>
            [SpreadsheetExportType.RawMaterial, SpreadsheetExportType.RawMaterialBatchNumber].includes(
              spreadsheetExportNotificationDetail.spreadsheetExportType,
            ),
          )
        ) {
          return 'Excel multiples - Matières';
        } else if (
          readSpreadsheetExportNotificationViewModel.spreadsheetExportNotificationDetails.every((spreadsheetExportNotificationDetail) =>
            [SpreadsheetExportType.Shade, SpreadsheetExportType.ShadeRawMaterial].includes(
              spreadsheetExportNotificationDetail.spreadsheetExportType,
            ),
          )
        ) {
          return 'Excel multiples - Teintes';
        } else if (
          readSpreadsheetExportNotificationViewModel.spreadsheetExportNotificationDetails.every((spreadsheetExportNotificationDetail) =>
            [
              SpreadsheetExportType.LaboratoryInformation,
              SpreadsheetExportType.LaboratoryInformationBank,
              SpreadsheetExportType.LaboratoryInformationContact,
              SpreadsheetExportType.LaboratoryInformationDeliveryRoute,
              SpreadsheetExportType.LaboratoryTaskNameSuggestion,
            ].includes(spreadsheetExportNotificationDetail.spreadsheetExportType),
          )
        ) {
          return 'Excel multiples - Paramètres du laboratoire';
        } else if (
          readSpreadsheetExportNotificationViewModel.spreadsheetExportNotificationDetails.every((spreadsheetExportNotificationDetail) =>
            [SpreadsheetExportType.LaboratoryUserCustomerSetting].includes(spreadsheetExportNotificationDetail.spreadsheetExportType),
          )
        ) {
          return 'Excel multiples - Paramètres des espaces clients';
        } else if (
          readSpreadsheetExportNotificationViewModel.spreadsheetExportNotificationDetails.every((spreadsheetExportNotificationDetail) =>
            [SpreadsheetExportType.LaboratoryTax].includes(spreadsheetExportNotificationDetail.spreadsheetExportType),
          )
        ) {
          return 'Excel multiples - Paramètre des taxes';
        } else if (
          readSpreadsheetExportNotificationViewModel.spreadsheetExportNotificationDetails.every((spreadsheetExportNotificationDetail) =>
            [SpreadsheetExportType.LaboratoryResource, SpreadsheetExportType.LaboratoryResourceContact].includes(
              spreadsheetExportNotificationDetail.spreadsheetExportType,
            ),
          )
        ) {
          return 'Excel multiples - Ressources';
        } else if (
          readSpreadsheetExportNotificationViewModel.spreadsheetExportNotificationDetails.every((spreadsheetExportNotificationDetail) =>
            [SpreadsheetExportType.SimpleFittingForm, SpreadsheetExportType.SimpleDetailedFittingForm].includes(
              spreadsheetExportNotificationDetail.spreadsheetExportType,
            ),
          )
        ) {
          return "PDF multiples - Listes des bons d'essayage";
        } else if (
          readSpreadsheetExportNotificationViewModel.spreadsheetExportNotificationDetails.every((spreadsheetExportNotificationDetail) =>
            [SpreadsheetExportType.SimpleDeliveryForm, SpreadsheetExportType.SimpleDetailedDeliveryForm].includes(
              spreadsheetExportNotificationDetail.spreadsheetExportType,
            ),
          )
        ) {
          if (
            readSpreadsheetExportNotificationViewModel.spreadsheetExportNotificationDetails.every((spreadsheetExportNotificationDetail) => {
              const deliveryFormFilter = SessionStorageService.deserializeJson(
                spreadsheetExportNotificationDetail.filter,
              ) as DeliveryFormFilter;
              if (deliveryFormFilter && deliveryFormFilter.invoicedFlag === true) {
                return true;
              } else {
                return false;
              }
            })
          ) {
            return 'PDF multiples - Listes des bons facturés';
          } else if (
            readSpreadsheetExportNotificationViewModel.spreadsheetExportNotificationDetails.every((spreadsheetExportNotificationDetail) => {
              const deliveryFormFilter = SessionStorageService.deserializeJson(
                spreadsheetExportNotificationDetail.filter,
              ) as DeliveryFormFilter;
              if (deliveryFormFilter && deliveryFormFilter.proFormaFlag === true) {
                return true;
              } else {
                return false;
              }
            })
          ) {
            return 'PDF multiples - Listes des bons sur pro forma';
          }
          return 'PDF multiples - Listes des bons de livraison';
        } else if (
          readSpreadsheetExportNotificationViewModel.spreadsheetExportNotificationDetails.every((spreadsheetExportNotificationDetail) =>
            [SpreadsheetExportType.SimpleQuote, SpreadsheetExportType.SimpleDetailedQuote].includes(
              spreadsheetExportNotificationDetail.spreadsheetExportType,
            ),
          )
        ) {
          return 'PDF multiples - Listes des devis';
        } else if (
          readSpreadsheetExportNotificationViewModel.spreadsheetExportNotificationDetails.every((spreadsheetExportNotificationDetail) =>
            [SpreadsheetExportType.SimpleWorkForm, SpreadsheetExportType.SimpleDetailedWorkForm].includes(
              spreadsheetExportNotificationDetail.spreadsheetExportType,
            ),
          )
        ) {
          return 'PDF multiples - Listes des bons de travail';
        } else {
          return 'Excel multiples';
        }
      }
    }
  }
}
